import { CheckCircleFillIcon, Flex, Heading, Paragraph, ProgressCircle } from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MAX_WORKSTYLES_POINTS } from "@/data/consultant-profiles";
import { WorkStyle } from "@/data/consultant-profiles/types/WorkStyle";
import { mapWorkStyleTypeToIcon } from "@/data/consultant-profiles/utils/mapWorkStyleTypeToIcon";
import { IChangeable } from "@/eworkitComponents";
import { IWithFormikFieldProps, withFormikField } from "@/formikComponents";
import { SingleWorkStyleEdit } from "@/views/consultant-profiles/details/work-styles/SingleWorkStyleEdit";
import { IWorkstylesFormSectionValues } from "@/views/ConsultantProfiles/Detail/WorkStylesSection/WorkstylesForm";

interface IWorkstyleSectionProps extends IWithFormikFieldProps, IChangeable {
  value: WorkStyle[];
  setFieldTouched: FormikProps<IWorkstylesFormSectionValues>["setFieldTouched"];
}

export const ApplyWorkStylesEdit: React.FC<IWorkstyleSectionProps> = ({ onChange, value, setFieldTouched }) => {
  const { t } = useTranslation("session");

  const pointsLeft = useMemo(() => {
    if (!value || value.length <= 0) {
      return MAX_WORKSTYLES_POINTS;
    } else {
      return MAX_WORKSTYLES_POINTS - value.reduce((acc, curr) => acc + curr.score, 0);
    }
  }, [value]);

  const handleChange = (type: string) => (score: number) => {
    if (!type) {
      return;
    }

    if (setFieldTouched) {
      setFieldTouched("workStyles", true);
    }

    if (onChange) {
      onChange([...value.filter(w => w.type !== type), { type, score }]);
    }
  };

  const resolution = pointsLeft > 0 ? "NOT_COMPLETED" : "COMPLETED";

  return (
    <Flex flexDirection="column" gap="sm">
      <Flex flexDirection="row" gap="md" p="sm" backgroundColor={pointsLeft > 0 ? "n50" : "g50"} borderRadius="lg">
        <Flex>
          {pointsLeft > 0 ? (
            <ProgressCircle
              aria-labelledby={t(`CONSULTANT_PROFILES.DETAIL.WORK_STYLES.DISTRIBUTION.${resolution}.HEADER`, {
                count: pointsLeft,
              })}
              value={((MAX_WORKSTYLES_POINTS - pointsLeft) / MAX_WORKSTYLES_POINTS) * 100}
              min={0}
              max={100}
              color="n500"
              size="sm"
            />
          ) : (
            <CheckCircleFillIcon color="g500" size="sm" />
          )}
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Heading size="h-xs">
            {t(`CONSULTANT_PROFILES.DETAIL.WORK_STYLES.DISTRIBUTION.${resolution}.HEADER`, { count: pointsLeft })}
          </Heading>
          <Paragraph>{t(`CONSULTANT_PROFILES.DETAIL.WORK_STYLES.DISTRIBUTION.${resolution}.DESCRIPTION`)}</Paragraph>
        </Flex>
      </Flex>

      {Object.entries(mapWorkStyleTypeToIcon).map(([type, WorkstyleIcon]) => (
        <SingleWorkStyleEdit
          type={type}
          WorkstyleIcon={WorkstyleIcon}
          score={value.find(w => w.type === type)?.score || 0}
          key={type}
          onChange={handleChange(type)}
          pointsLeft={pointsLeft}
        />
      ))}
    </Flex>
  );
};

export const FormikApplyWorkStylesEdit = withFormikField(ApplyWorkStylesEdit);
